import { Logger } from '@gitbook/util-debug';
import { normalizeURLSlugBasename, validateURLSlugBasename } from '@gitbook/util-url';

import { normalizeReusableContentTitle } from '../../reusableContent';
import { normalizeTagLabel, VALID_TAG_COLORS } from '../../tags';
import type { TagColor } from '../../types';
import {
    type Revision,
    type RevisionConflicts,
    type RevisionPage,
    type RevisionPageComputed,
    type RevisionPageDocument,
    type RevisionPageLink,
    type RevisionPageMetaLink,
    type RevisionPageMetaLinks,
    type RevisionPageParent,
    type RevisionPageTag,
    RevisionPageType,
    type RevisionReusableContents,
    type RevisionTags,
    RevisionType,
    type RevisionVariables,
} from '../../types';
import { type Edits, isYValue, mutations, yToJSON } from '../../utils';
import type { MutableData } from '../../utils/yjs/mutations';
import { validateVariableName } from '../variables';
import { normalizeTagGroupsInRevision } from './tagGroups';

const logger = Logger('normalizeRevision');

/**
 * The schema version of the normalization process.
 * Change it when the normalization process changes in a breakin way that should require all revisions to be re-normalized.
 */
export const REVISION_SCHEMA_VERSION = 2;

/**
 * Cleans up a Revision from invalid data, such as:
 * - pages, sub-pages of pages and root pages without parent
 * - non-existing conflicting pages
 *
 * It returns true if a change was made to the revision.
 */
export function normalizeRevision(
    revision: mutations.MutableData<Revision>,
    options: {
        /**
         * If true, if the schema version of the revision is equals to the current one, the normalization will be skipped.
         * @default false
         */
        checkSchemaVersion?: boolean;

        /**
         * If true, the schema version of the revision will be updated to the current one.
         * It should only be set to true when we know that the revision will not be mutated again.
         * For safety, during development, the revision will be frozen after the schema version is updated.
         * @default false
         */
        updateSchemaVersion?: boolean;
    } = {}
): boolean {
    const { checkSchemaVersion = false, updateSchemaVersion = false } = options;

    if (checkSchemaVersion) {
        const schemaVersion = mutations.get(revision, 'schemaVersion');
        if (schemaVersion === REVISION_SCHEMA_VERSION) {
            return false;
        }
    }

    const id = mutations.get(revision, 'id');
    const contentId = mutations.get(revision, 'contentId');

    try {
        let changed = false;

        // Validate and normalize revision type
        const type = mutations.get(revision, 'type');
        if (type === RevisionType.Merge && !mutations.get(revision, 'mergedFrom')) {
            changed = true;
            mutations.set(revision, 'type', RevisionType.Update);
        }

        const pages = mutations.get(revision, 'pages');
        const files = mutations.get(revision, 'files');
        const conflicts = mutations.get(revision, 'conflicts');
        const parents = mutations.get(revision, 'parents');
        const pageIds = new Set(mutations.keys(pages));

        if (!parents) {
            throw new Error(`Revision ${contentId}/${id} has no parents`);
        }

        // Validate and normalize parents
        if (parents.length > 3) {
            throw new Error(`Revision ${contentId}/${id} has more than 3 parents`);
        }
        const parentsArray = mutations.values(parents);
        if (parentsArray.includes(id)) {
            const newParents = parentsArray.filter((parentId) => parentId !== id);
            if (newParents.length === 0) {
                throw new Error(
                    `Revision ${contentId}/${id} has no more parents after removing itself as parent`
                );
            }
            changed = true;
            mutations.updateInArray(parents, newParents);

            logger.warning(
                `${contentId}/${id}: updated parents to remove itself as parent. New parents:`,
                newParents
            );
        }

        // Validate and normalize revision edits
        const edits = mutations.get(revision, 'edits');
        if (normalizeRevisionEdits(edits, contentId, id)) {
            changed = true;
        }

        // Validate and normalize variables
        const variables = mutations.get(revision, 'variables');
        if (variables) {
            if (normalizeVariables(variables)) {
                changed = true;
            }
        }

        // Validate and normalize reusable content
        const reusableContents = mutations.get(revision, 'reusableContents');
        if (!reusableContents) {
            changed = true;
            mutations.set(revision, 'reusableContents', {});
        } else {
            if (normalizeReusableContents(reusableContents)) {
                changed = true;
            }
        }

        if (normalizeTagGroupsInRevision(revision)) changed = true;

        // Validate and normalize tags
        const tags = mutations.get(revision, 'tags');
        if (tags) {
            if (normalizeTags(tags)) {
                changed = true;
            }
        }

        const normalizePages = (
            pages: string[] | mutations.MutableData<string[]>,
            isRoot: boolean = false
        ): string[] | undefined => {
            const validPages: string[] = [];
            pages.forEach((pageId: string) => {
                if (normalizePage(pageId, isRoot)) {
                    validPages.push(pageId);
                }
            });

            if (validPages.length === pages.length) {
                return undefined;
            }

            return validPages;
        };

        const normalizeFile = (fileId: string) => {
            const file = mutations.get(files, fileId);
            if (!file) {
                return;
            }

            const downloadURL = mutations.get(file, 'downloadURL');
            if (!downloadURL) {
                changed = true;
                // Some old files might not have a downloadURL, we should remove them
                mutations.del(files, fileId);

                logger.warning(`${contentId}/${id}: no downloadURL for file ${fileId}`);
                return;
            }
        };

        const normalizePage = (pageId: string, isRoot: boolean = false): boolean => {
            if (!pageIds.has(pageId)) {
                return false;
            }

            pageIds.delete(pageId);

            const page = mutations.get(pages, pageId);
            if (!page) {
                return false;
            }

            // Validate and normalize title
            const title = mutations.get(page, 'title');
            if (typeof title !== 'string' || title.length === 0) {
                changed = true;
                mutations.set(page, 'title', 'Untitled');
                return true;
            }

            if (mutations.get(page, 'type') === RevisionPageType.Link) {
                if (
                    normalizePageLink(
                        contentId,
                        id,
                        pageId,
                        page as mutations.MutableData<RevisionPageLink>
                    )
                ) {
                    changed = true;
                }
                return true;
            }

            if (mutations.get(page, 'type') === RevisionPageType.Computed) {
                if (
                    normalizePageComputed(
                        contentId,
                        id,
                        pageId,
                        page as mutations.MutableData<RevisionPageComputed>
                    )
                ) {
                    changed = true;
                }
                return true;
            }

            // Groups should only be at root level, convert page to a document if not at root
            // it will keep the structure while removing the invalid group
            if (!isRoot && mutations.get(page, 'type') === RevisionPageType.Group) {
                changed = true;
                mutations.set(page, 'type', RevisionPageType.Document);
                logger.warning(
                    `${contentId}/${id}: converted group page ${pageId} to document because it is not at root level.`
                );
            }

            if (checkIsRevisionPageDocument(page)) {
                // Some slugs might be invalid due to the issue fixed by https://github.com/GitbookIO/gitbook-x/pull/10821
                // Normalize the page slug if needed so future revisions are fixed
                if (mutations.get(page, 'slug')) {
                    if (normalizePageSlug(contentId, id, pageId, page)) {
                        changed = true;
                    }
                }

                // Validate and normalize variables
                const variables = mutations.get(page, 'variables');
                if (variables) {
                    if (normalizeVariables(variables)) {
                        changed = true;
                    }
                }

                // Validate and normalize metaLinks
                const metaLinks = mutations.get(page, 'metaLinks');
                if (metaLinks) {
                    if (normalizeMetaLinks(metaLinks)) {
                        changed = true;
                    }
                }

                // Validate and normalize page tags
                const tags = mutations.get(page, 'tags');
                if (tags) {
                    if (normalizePageTags(tags)) {
                        changed = true;
                    }
                }
            }

            if (checkIsRevisionPageParent(page)) {
                let childrenPages = mutations.get(page, 'pages');

                // Ensure that every parent page has a pages array, even if empty.
                if (childrenPages === undefined) {
                    changed = true;
                    mutations.set(page, 'pages', []);
                    childrenPages = mutations.get(page, 'pages');
                }

                const normalizedChildrenPages = normalizePages(childrenPages);
                if (normalizedChildrenPages) {
                    changed = true;
                    mutations.updateInArray(childrenPages, normalizedChildrenPages);

                    logger.warning(
                        `${contentId}/${id}: updated children for page ${pageId}. New children pages:`,
                        normalizedChildrenPages
                    );
                }
            }

            return true;
        };

        // rootPages should aways be defined.
        let rootPages = mutations.get(revision, 'rootPages');
        if (rootPages === undefined) {
            changed = true;
            mutations.set(revision, 'rootPages', []);
            rootPages = mutations.get(revision, 'rootPages');
        }

        // Validate and normalize rootPages, this will also normalize all of their children pages.
        const normalizedRootPages =
            // Put all pageIds in rootPages if it's empty, the cleanup will be done later in the logic
            // once iterated on all pages we will remove non root pages from rootPages.
            normalizePages(rootPages.length === 0 ? Array.from(pageIds) : rootPages, true);
        if (normalizedRootPages) {
            changed = true;
            mutations.updateInArray(rootPages, normalizedRootPages);

            logger.warning(
                `${contentId}/${id}: normalized rootPages. New root pages:`,
                normalizedRootPages
            );
        }

        // Make pages that don't have a valid parent into rootPages
        if (pageIds.size > 0) {
            const newRootPageIds = Array.from(pageIds);
            mutations.push(rootPages, newRootPageIds);
            changed = true;

            logger.warning(
                `${contentId}/${id}: made pages with invalid parent into rootPages. Pages:`,
                newRootPageIds
            );
        }

        // Validate all the files
        for (const fileId of mutations.keys(files)) {
            normalizeFile(fileId);
        }

        // Validate that in conflicts we are not referencing no-longer existing pages and page links.
        // We also remove the conflicts in case all have been resolved.
        if (conflicts !== undefined) {
            const conflictPages = mutations.get(conflicts as RevisionConflicts, 'pages');
            const conflictPageIds = mutations.keys(conflictPages);

            for (const pageId of conflictPageIds) {
                const conflictPage = mutations.get(pages, pageId);
                // If the page is not found in the revision, or is not a document,
                // we remove it from the conflicts
                if (
                    !conflictPage ||
                    mutations.get(conflictPage, 'type') !== RevisionPageType.Document
                ) {
                    changed = true;
                    mutations.del(conflictPages, pageId);
                }
            }

            // If after removing all invalid conflicts, there are no conflicts left, we remove the conflicts property
            if (mutations.keys(conflictPages).length === 0) {
                changed = true;
                mutations.del(revision, 'conflicts');

                logger.warning(
                    `${contentId}/${id}: removed conflicts. All were invalid and have been removed.`
                );
            } else {
                // And if we're left with only resolved conflicts, we remove them as well
                if (
                    mutations
                        .values(conflictPages)
                        .every((conflictState) => conflictState === false)
                ) {
                    changed = true;
                    mutations.del(revision, 'conflicts');

                    logger.warning(
                        `${contentId}/${id}: removed conflicts. All were resolved and have been removed.`
                    );
                }
            }
        }

        if (
            updateSchemaVersion &&
            mutations.get(revision, 'schemaVersion') !== REVISION_SCHEMA_VERSION
        ) {
            if (isYValue(revision)) {
                throw new Error(
                    `Cannot update schema version of revision ${contentId}/${id} because it is a YValue`
                );
            }

            changed = true;
            mutations.set(revision, 'schemaVersion', REVISION_SCHEMA_VERSION);

            if (process.env.NODE_ENV !== 'production') {
                deepFreeze(revision);
            }
        }

        return changed;
    } catch (error) {
        logger.error('Failed to normalize revision', {
            contentId,
            error,
            revisionId: id,
        });
        throw error;
    }
}

function normalizeRevisionEdits(
    edits: MutableData<Edits>,
    contentId: string,
    revisionId: string
): boolean {
    let changed = false;

    for (const [userId, edit] of mutations.entries(edits)) {
        const normalizedUpdatedAt = normalizeEditUpdatedAt(mutations.get(edit, 'updatedAt'));

        if (normalizedUpdatedAt === null) {
            changed = true;
            mutations.del(edits, userId);
            logger.warning(
                `${contentId}/${revisionId}: removed edit for user ${userId} with invalid updatedAt value.`
            );
            continue;
        }

        if (normalizedUpdatedAt !== mutations.get(edit, 'updatedAt')) {
            changed = true;
            mutations.setIn(edits, [userId, 'updatedAt'] as const, normalizedUpdatedAt);
            logger.warning(
                `${contentId}/${revisionId}: normalized edit timestamp for user ${userId}.`
            );
        }
    }

    return changed;
}

function normalizeEditUpdatedAt(value: unknown): number | null {
    if (typeof value === 'number') {
        return isValidDateValue(value) ? value : null;
    }

    if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value.getTime() : null;
    }

    if (typeof value === 'string') {
        const parsedNumber = parseInt(value, 10);
        return isValidDateValue(parsedNumber) ? parsedNumber : null;
    }

    return null;
}

function isValidDateValue(value: number): boolean {
    return Number.isFinite(value) && Number.isFinite(new Date(value).getTime());
}

function checkIsRevisionPageDocument(
    page: mutations.MutableData<RevisionPage>
): page is mutations.MutableData<RevisionPageDocument> {
    return mutations.get(page, 'type') === RevisionPageType.Document;
}

function checkIsRevisionPageParent(
    page: mutations.MutableData<RevisionPage>
): page is mutations.MutableData<RevisionPageParent> {
    return (
        mutations.get(page, 'type') === RevisionPageType.Document ||
        mutations.get(page, 'type') === RevisionPageType.Group
    );
}

/**
 * Normalize page or revision variables.
 */
export function normalizeVariables(variables: mutations.MutableData<RevisionVariables>): boolean {
    let changed = false;
    for (const [name, value] of mutations.entries(variables)) {
        if (
            typeof name !== 'string' ||
            !validateVariableName(name) ||
            (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')
        ) {
            changed = true;
            mutations.del(variables, name);
        }
    }

    return changed;
}

/**
 * Normalize page meta links
 */
export function normalizeMetaLinks(
    metaLinks: mutations.MutableData<RevisionPageMetaLinks>
): boolean {
    let changed = false;

    // Normalize canonical link
    const canonical = mutations.get(metaLinks, 'canonical');
    if (canonical) {
        const kind = mutations.get(
            canonical as mutations.MutableData<RevisionPageMetaLink>,
            'kind'
        );
        if (kind !== 'page' && kind !== 'url') {
            changed = true;
            mutations.del(metaLinks, 'canonical');
        }
    }

    // Normalize alternates array
    const alternates = mutations.get(metaLinks, 'alternates');
    if (alternates) {
        const validAlternates: RevisionPageMetaLink[] = [];
        for (const alternate of mutations.values(alternates)) {
            const kind = mutations.get(alternate, 'kind');
            if (kind === 'page' || kind === 'url') {
                validAlternates.push(isYValue(alternate) ? yToJSON(alternate) : alternate);
            } else {
                changed = true;
            }
        }

        if (validAlternates.length !== alternates.length) {
            changed = true;
            mutations.updateInArray(alternates, validAlternates);
        }

        // If all alternates were invalid, remove the alternates property
        if (validAlternates.length === 0) {
            changed = true;
            mutations.del(metaLinks, 'alternates');
        }
    }

    return changed;
}

/**
 * Normalize reusable contents.
 */
function normalizeReusableContents(
    reusableContents: mutations.MutableData<RevisionReusableContents>
): boolean {
    let changed = false;
    for (const [_, reusableContent] of mutations.entries(reusableContents)) {
        // Validate and normalize title
        const title = mutations.get(reusableContent, 'title');
        if (typeof title !== 'string' || title.length === 0) {
            changed = true;
            mutations.set(reusableContent, 'title', 'Untitled');
            continue;
        }

        const normalizedTitle = normalizeReusableContentTitle(title);
        if (normalizedTitle !== title) {
            changed = true;
            mutations.set(reusableContent, 'title', normalizedTitle);
        }
    }

    return changed;
}

/**
 * Normalize tags.
 * - Trims whitespace and enforces max length on labels
 * - Removes tags with invalid labels (empty or non-string after trimming)
 * - Removes duplicate tags by slug (keeps first occurrence)
 */
function normalizeTags(tags: mutations.MutableData<RevisionTags>): boolean {
    let changed = false;
    const seenSlugs = new Set<string>();

    for (const [tagKey, tag] of mutations.entries(tags)) {
        const slug = String(tagKey);
        // Deduplicate by slug (keep first occurrence)
        if (seenSlugs.has(slug)) {
            changed = true;
            mutations.del(tags, tagKey);
            continue;
        }
        seenSlugs.add(slug);

        // Validate label
        const label = mutations.get(tag, 'label');
        if (typeof label !== 'string') {
            changed = true;
            mutations.del(tags, tagKey);
            continue;
        }

        // Normalize label (trim whitespace + enforce max length) before checking emptiness
        const normalizedLabel = normalizeTagLabel(label);
        if (normalizedLabel.length === 0) {
            changed = true;
            mutations.del(tags, tagKey);
            continue;
        }

        if (normalizedLabel !== label) {
            changed = true;
            mutations.set(tag, 'label', normalizedLabel);
        }

        // Validate emoji (must be string or absent)
        const emoji = mutations.get(tag, 'emoji');
        if (emoji !== undefined && typeof emoji !== 'string') {
            changed = true;
            mutations.del(tag, 'emoji');
        }

        // Validate icon (must be string or absent)
        const icon = mutations.get(tag, 'icon');
        if (icon !== undefined && typeof icon !== 'string') {
            changed = true;
            mutations.del(tag, 'icon');
        }

        // Emoji and icon are mutually exclusive — keep icon if both are present. We re-define the emoji/icons values to ensure that if emoji was valid and icon was not, the emoji prevails despite not being the one that takes precedence. If we'd use the stale local variables from above, we'd lose both.
        const validEmoji = mutations.get(tag, 'emoji');
        const validIcon = mutations.get(tag, 'icon');
        if (validEmoji && validIcon) {
            changed = true;
            mutations.del(tag, 'emoji');
        }

        // Validate color against DocumentTextColor enum values
        const color = mutations.get(tag, 'color');
        if (
            color === undefined ||
            typeof color !== 'string' ||
            !VALID_TAG_COLORS.has(color as TagColor)
        ) {
            changed = true;
            mutations.set(tag, 'color', 'default');
        }
    }

    return changed;
}

/**
 * Normalize page tags in place.
 * - Removes invalid tag references
 * - Deduplicates by tag slug (keeps first occurrence)
 * - Ensures only one tag has primary flag (keeps first, removes from others)
 */
export function normalizePageTags(tags: mutations.MutableData<RevisionPageTag[]>): boolean {
    if (tags === undefined) {
        return false;
    }

    const isArray = Array.isArray(tags) || isYValue(tags);
    if (!isArray) {
        return false;
    }

    const length = Array.isArray(tags) ? tags.length : (tags as any).length;
    if (length === 0) {
        return false;
    }

    const seen = new Set<string>();
    let hasPrimary = false;
    let madeChanges = false;
    const validTagRefs: RevisionPageTag[] = [];

    for (const tagRef of mutations.values(tags)) {
        if (!tagRef || typeof tagRef !== 'object') {
            continue;
        }

        const tag = mutations.get(tagRef as any, 'tag');
        if (!tag || typeof tag !== 'object') {
            continue;
        }

        const kind = mutations.get(tag, 'kind');
        const tagSlug = mutations.get(tag, 'tag');
        if ((kind !== undefined && kind !== 'tag') || !tagSlug || typeof tagSlug !== 'string') {
            continue;
        }

        if (seen.has(tagSlug)) {
            continue;
        }
        seen.add(tagSlug);

        if (kind !== 'tag') {
            madeChanges = true;
        }

        const primary = mutations.get(tagRef as any, 'primary');
        const normalizedTagRef: RevisionPageTag = {
            tag: { kind: 'tag', tag: tagSlug },
            ...(primary && !hasPrimary ? { primary: true } : {}),
        };

        if (primary && hasPrimary) {
            madeChanges = true;
        } else if (primary) {
            hasPrimary = true;
        }

        validTagRefs.push(normalizedTagRef);
    }

    if (madeChanges || validTagRefs.length !== length) {
        mutations.updateInArray(tags, validTagRefs);
        return true;
    }

    return false;
}

/**
 * Normalize a page link.
 */
function normalizePageLink(
    contentId: string,
    revisionId: string,
    pageId: string,
    pageLink: mutations.MutableData<RevisionPageLink>
): boolean {
    const target = mutations.get(pageLink, 'target');
    const targetKind = typeof target !== 'object' ? undefined : mutations.get(target, 'kind');
    const targetSpace = typeof target !== 'object' ? undefined : mutations.get(target, 'space');

    if (typeof targetKind !== 'string') {
        logger.warning(
            `${contentId}/${revisionId}: normalized invalid link ${pageId}, with target ${JSON.stringify(
                yToJSON(target)
            )}`
        );

        // Invalid target, convert page to a document
        // @ts-ignore
        mutations.set(pageLink, 'type', RevisionPageType.Document);
        // @ts-ignore
        mutations.set(pageLink, 'pages', []);
        mutations.del(pageLink, 'target');

        return true;
    }
    if (targetKind === 'collection' && typeof targetSpace !== 'undefined') {
        // Autofix for https://linear.app/gitbook-x/issue/RND-4519/space-w8zhwnvbxt3w0rdsfdqk-crash-firebaseerror-function-doc-cannot-be
        // Fix for the original issue leading to invalid revisions in https://github.com/GitbookIO/gitbook-x/pull/13254
        // Because this issue was caused by a bug in the codebase and might have affected multiple revisions, we can't fix it everywhere
        // so we use this autofix to prevent the crash and allowing the users to have a valid revision again
        mutations.del(target, 'space');
        mutations.set(target, 'collection', targetSpace);
        mutations.set(pageLink, 'target', target);

        return true;
    }

    return false;
}

/**
 * Normalize a page computed.
 */
function normalizePageComputed(
    _contentId: string,
    _revisionId: string,
    _pageId: string,
    pageComputed: mutations.MutableData<RevisionPageComputed>
): boolean {
    const type = mutations.getIn(pageComputed, ['computed', 'type'] as const);

    // @ts-ignore - this is what we want to normalize
    if (type === 'integration:openapi:generate' || type === 'core:openapi') {
        mutations.setIn(pageComputed, ['computed', 'type'] as const, 'builtin:openapi');
        return true;
    }

    // @ts-expect-error - this is what we want to normalize
    const integration = mutations.getIn(pageComputed, ['computed', 'integration'] as const);
    // @ts-expect-error - this is what we want to normalize
    const source = mutations.getIn(pageComputed, ['computed', 'source'] as const);

    if (typeof integration === 'string' && typeof source === 'string') {
        if (integration === 'openapi' && source === 'generate') {
            mutations.setIn(pageComputed, ['computed', 'type'] as const, 'builtin:openapi');
        } else {
            mutations.setIn(
                pageComputed,
                ['computed', 'type'] as const,
                `integration:${integration}:${source}`
            );
        }
        // @ts-expect-error - this is what we want to normalize
        mutations.delIn(pageComputed, ['computed', 'source'] as const);
        // @ts-expect-error - this is what we want to normalize
        mutations.delIn(pageComputed, ['computed', 'integration'] as const);
        return true;
    }

    // Nothing to do for now
    return false;
}

/**
 * Normalize a page slug.
 */
function normalizePageSlug(
    contentId: string,
    revisionId: string,
    pageId: string,
    pageDocument: mutations.MutableData<RevisionPageDocument>
): boolean {
    const slug = mutations.get(pageDocument, 'slug');
    if (!slug || validateURLSlugBasename(slug)) {
        return false;
    }

    const normalized = normalizeURLSlugBasename(slug);
    mutations.set(pageDocument, 'slug', normalized);
    logger.warning(
        `Normalized invalid slug ${slug} as ${normalized}. contentId=${contentId} revisionId=${revisionId} pageId=${pageId}`
    );

    return true;
}

function deepFreeze(o: object) {
    for (const key in o) {
        const c = (o as Record<string, unknown>)[key];
        if (typeof c === 'object' || typeof c === 'function') {
            deepFreeze(c as object);
        }
    }
    return Object.freeze(o);
}
